import {
	useState,
	useEffect,
	useRef,
	RefObject,
	MutableRefObject,
	useCallback,
	useLayoutEffect,
	useMemo,
} from "react";
import { AnimationFast } from "@siteimprove/fancylib/design-tokens/variables";

/**
 * A hook that detects clicks outside of a specified element
 * @param ref - object
 * @param handler - an event
 * @returns - listens for events outside of this element that will affect it
 * @see https://usehooks.com/useOnClickOutside/
 */
export function useOnClickOutside<T extends HTMLElement>(
	ref: RefObject<T>,
	handler: (event: MouseEvent | TouchEvent) => void,
	deps: unknown[] = []
): void {
	useEffect(() => {
		const listener = (event: MouseEvent | TouchEvent) => {
			// Call handler if ref's element is not a decedent
			if (event.target instanceof Node && ref.current && !ref.current.contains(event.target)) {
				handler(event);
			}
		};
		document.addEventListener("mousedown", listener);
		document.addEventListener("touchstart", listener);
		return () => {
			document.removeEventListener("mousedown", listener);
			document.removeEventListener("touchstart", listener);
		};
	}, [ref, ...deps]);
}

/**
 * Exactly like useEffect, except it does not trigger on initial render.
 * @param effect effect passed to useEffect, but is not called on initial render.
 * @param deps dependencies passed to useEffect.
 * @see React.useEffect
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDidUpdate(effect: React.EffectCallback, deps?: readonly any[]): void {
	const isInitial = useRef(true);
	useEffect(() => {
		if (!isInitial.current) {
			effect();
		}
		isInitial.current = false;
	}, deps);
}

/**
 * A hook that behaves similar to useState, but has a built in throttling behavior for a performance boost.
 * @param initialValue - Initial state, exactly like useState.
 * @param wait - The minimum number of milliseconds between updates to `throttledVal`. Defaults to 100.
 * @returns Tuple with (throttledVal: use to render value to UI. liveVal: use to allow updating the value faster than throttle. setLiveVal: sets live val and triggers a process that will update throttledVal according to the throttle behavior)
 */
export function useThrottledState<T>(
	initialValue: T,
	wait = 100
): [T, MutableRefObject<T>, (newVal: T) => void] {
	const [throttledVal, setthrottledVal] = useState(initialValue);
	const liveVal = useRef(initialValue);
	const timeout = useRef(null as NodeJS.Timeout | null);
	const then = useRef(0);

	// We need to clear the timeout if the component dismounts while the timeout exists
	useEffect(() => {
		return () => {
			timeout.current && clearTimeout(timeout.current);
		};
	});

	const throttleValue = (val: T) => {
		const now = +new Date();
		const remainingWait = then.current + wait - now;

		// We always "reset" the timeout each time the liveValue changed, because either the value will be updated immediately, or a new timeout will be started
		timeout.current && clearTimeout(timeout.current);

		// We set the throttledVal if enough time has passed since last update
		if (remainingWait <= 0) {
			setthrottledVal(val);
			then.current = now;
		}

		// Not enough time had passed, so we set a timeout to wait the remaining duration
		else {
			timeout.current = setTimeout(() => {
				setthrottledVal(liveVal.current);
				then.current = +new Date();
			}, remainingWait);
		}
	};

	return [
		throttledVal,
		liveVal,
		(newVal: T) => {
			liveVal.current = newVal;
			throttleValue(newVal);
		},
	];
}

/**
 * Much like `useOnClickOutside`, this hook allows you to add behavior when `focusin` event occurs outside ref and it's children.
 * @param ref - The element ref that focus outside will trigger on.
 * @param handler - the closure called when `focusin` event occurs outside ref and it's children.
 * @param deps - Optional dependencies for the internal `useEffect` in this hook. Useful if the closure needs to be re-applied,
 * to avoid a stale closure. `ref` is always added as a dependency.
 */
export function useOnFocusOutside<T extends HTMLElement>(
	ref: RefObject<T>,
	handler: (event: FocusEvent) => void,
	deps?: unknown[]
): void {
	deps = deps || [];
	useEffect(() => {
		const listener = ((event: FocusEvent) => {
			// Call handler if ref's element is not a decendant
			if (
				event.relatedTarget instanceof Node &&
				ref.current &&
				!ref.current.contains(event.relatedTarget)
			) {
				handler(event);
			}
		}) as EventListener;
		document.addEventListener("focusin", listener);
		return () => {
			document.removeEventListener("focusin", listener);
		};
	}, [ref, ...deps]);
}

/**
 * Used to trigger a re-run of any useEffect or useLayoutEffect, that you pass the dependency variable to.
 * @returns object containing a dependency variable to pass to hooks with deps array, and a function to trigger the re-render.
 */
export function useReRender(): { dependency: number; triggerRender: () => void } {
	const [dependency, setDependency] = useState(0);
	return {
		dependency,
		triggerRender: () => setDependency(dependency + 1),
	};
}

/**
 * Used to measure DOM nodes. It takes a reference to the node you want to
 * measure and uses a Resize Observer to update the current dimensions of the
 * target node which are then returned by the hook.
 * @returns array containing the dimensions [width, height] of the DOM node
 * used as reference.
 */
export function useElementSize<T extends HTMLElement>(ref: RefObject<T>): [number, number] {
	const [size, setSize] = useState<{ width: number; height: number }>({
		width: 0,
		height: 0,
	});

	// Prevent too many rendering using useCallback
	const handleSize = useCallback(() => {
		setSize({
			width: ref.current?.offsetWidth || 0,
			height: ref.current?.offsetHeight || 0,
		});
	}, [ref.current?.offsetHeight, ref.current?.offsetWidth]);

	useEffect(() => {
		window.addEventListener("resize", handleSize);
		return () => window.removeEventListener("resize", handleSize);
	}, []);

	useLayoutEffect(() => {
		handleSize();
	}, []);

	return [size.width, size.height];
}

/**
 * Used to filter out items from a list based on a predicate.
 * @returns an array of items that passed the predicate.
 */
export function useSearchableList<T>(
	items: T[],
	search: string,
	stringify?: (item: T) => string
): T[] {
	const searchableItems = useMemo(() => {
		return items.filter((item) => {
			const itemString = (stringify || String)(item);
			return itemString.toLowerCase().includes(search.toLowerCase());
		});
	}, [items, search]);
	return searchableItems;
}

/**
 * Used to toggle between collapsed and expanded states of an element.
 * It will automatically transition the height of the element.
 * @param ref - The element ref that will be collapsed or expanded.
 * @param isExpanded - A boolean that indicates if the element should be expanded.
 * @param isTransitionEnabled - A boolean that indicates if the transition should be enabled.
 * @see https://css-tricks.com/using-css-transitions-auto-dimensions/
 */
export function useVerticalExpandCollapse<T extends HTMLElement>(
	ref: RefObject<T>,
	isExpanded: boolean,
	isTransitionEnabled = true,
	delay = AnimationFast
): void {
	const transition = isTransitionEnabled ? `height ${delay}` : "none";

	useEffect(() => {
		if (ref.current) {
			isExpanded ? expand(ref.current) : collapse(ref.current);
		}
	}, [isExpanded]);

	function collapse(element: T) {
		if (!isTransitionEnabled) {
			element.style.display = "none";
			element.style.height = "0";
			return;
		}

		// get the height of the element's inner content, regardless of its actual size
		const sectionHeight = element.scrollHeight;

		// temporarily disable all css transitions
		element.style.transition = "";

		// on the next frame (as soon as the previous style change has taken effect),
		// explicitly set the element's height to its current pixel height, so we
		// aren't transitioning out of 'auto'
		requestAnimationFrame(function () {
			element.style.height = sectionHeight + "px";
			element.style.transition = transition;

			// on the next frame (as soon as the previous style change has taken effect),
			// have the element transition to height: 0
			requestAnimationFrame(function () {
				element.style.height = "0";

				element.addEventListener(
					"transitionend",
					function handler() {
						// set the element's display to none, so it cannot be focused or tabbed to
						element.style.display = "none";
						element.style.height = "0";
					},
					{ once: true }
				);
			});
		});
	}

	function expand(element: T) {
		if (!isTransitionEnabled) {
			element.style.display = "block";
			element.style.height = "auto";
			return;
		}

		requestAnimationFrame(function () {
			element.style.display = "block";

			requestAnimationFrame(function () {
				// get the height of the element's inner content, regardless of its actual size
				const sectionHeight = element.scrollHeight;
				// have the element transition to the height of its inner content
				element.style.height = sectionHeight + "px";
				element.style.transition = transition;

				// when the next css transition finishes (which should be the one we just triggered)
				element.addEventListener(
					"transitionend",
					function handler() {
						// remove "height" from the element's inline styles, so it can return to its initial value
						element.style.height = null as unknown as string;

						// remove "display" from the element's inline styles, so it can return to its initial value
						element.style.display = null as unknown as string;
					},
					{ once: true }
				);
			});
		});
	}
}

/*
 * useMultiToggle is a custom hook that manages a boolean state
 * for multiple independent callers. The state is true if any caller
 * has set it to true, and only turns false when all callers have set it to false.
 * An initial state can be provided.
 */
export function useMultiToggle(initialState = false): [boolean, (value: boolean) => void] {
	const [state, setState] = useState(initialState);
	const count = useRef(initialState ? 1 : 0);

	const setMultiState = (value: boolean) => {
		count.current += value ? 1 : -1;
		setState(count.current > 0);
	};

	return [state, setMultiState];
}
